<script lang="ts">
  import FakeScrollbar from './FakeScrollbar.svelte';
  import { onMount, type Snippet } from 'svelte';
  import { pxInContext } from '#lib/utils/cssUnitConvert.js';
  import type { ClassValue } from 'svelte/elements';

  interface Props {
    /** Explicit width per column in order: [checkbox?, alert?, name, ...data cols] */
    columnWidths: (number | string)[];
    /** Total pixel height of all virtual rows (for the tbody spacer). */
    totalSize: number;
    stickyHeader?: boolean;
    hideBottom?: boolean;
    disableStickyScrollbar?: boolean;
    classes?: {
      mainWrapper?: ClassValue;
      bottomWrapper?: ClassValue;
    };
    styles?: {
      mainWrapper?: string;
    };
    /** @bind read only */
    wrapper?: HTMLElement;
    header?: Snippet;
    children?: Snippet;
    bottomHeader?: Snippet;
    bottomBody?: Snippet;
  }

  let {
    columnWidths,
    totalSize,
    stickyHeader = false,
    hideBottom = false,
    disableStickyScrollbar = false,
    styles = {},
    classes = {},
    wrapper = $bindable(undefined),
    header,
    children,
    bottomHeader,
    bottomBody,
  }: Props = $props();

  // Minimum table width is the sum of all numeric column widths.
  const totalWidth = $derived(
    columnWidths.reduce((sum: number, w) => sum + (typeof w === 'number' ? w : 0), 0),
  );

  // ─── Sticky header / bottom ────────────────────────────────────

  let fakebarVisible = $state(false);
  let showTop = $state(false);
  let top = $state<HTMLElement | undefined>();
  let bottom = $state<HTMLElement | undefined>();
  let showBottom = $derived(
    !hideBottom && (bottomHeader !== undefined || bottomBody !== undefined),
  );

  /** Fixed .top / .bottom layers must match the horizontal scroll wrapper, not the viewport. */
  const syncFixedLayerBox = () => {
    if (!wrapper) return;

    const { left } = wrapper.getBoundingClientRect();
    const l = Math.round(left);

    if (top) top.style.left = `${l}px`;
    if (bottom) bottom.style.left = `${l}px`;
  };

  onMount(() => {
    const main = document.querySelector('main');
    if (!main) return;

    const mainScroll = () => {
      const mainRect = main.getBoundingClientRect();
      const tableRect = wrapper?.querySelector(':scope > table')?.getBoundingClientRect();
      if (!tableRect) return;

      fakebarVisible =
        !disableStickyScrollbar &&
        tableRect.bottom > mainRect.bottom &&
        tableRect.top < mainRect.bottom;

      const offsetHeight = pxInContext(wrapper!, 'var(--header-offset)');
      showTop = stickyHeader && tableRect.top - 24 - offsetHeight < mainRect.top;

      syncFixedLayerBox();
    };

    mainScroll();
    main.addEventListener('scroll', mainScroll);
    return () => main.removeEventListener('scroll', mainScroll);
  });

  $effect(() => {
    if (!wrapper) return;
    top;
    bottom;

    syncFixedLayerBox();
    const callSync = () => syncFixedLayerBox();

    window.addEventListener('resize', callSync);
    const ro = new ResizeObserver(callSync);
    ro.observe(wrapper);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', callSync);
    };
  });

  let leftScroll = 0;
  let ticking = false;
  const wrapperScroll = () => {
    leftScroll = wrapper?.scrollLeft ?? 0;
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        syncFixedLayerBox();
        if (top) top.style.transform = `translateX(-${leftScroll}px)`;
        if (bottom) bottom.style.transform = `translateX(-${leftScroll}px)`;
      });
    }
  };

  onMount(wrapperScroll);

  // ─── Column hover highlight ─────────────────────────────────────

  const countColumns = (
    row: HTMLTableRowElement,
    until: (el: HTMLTableCellElement, i: number) => boolean = () => false,
  ): [col: HTMLTableCellElement, index: number] => {
    const cols = [...row.children] as HTMLTableCellElement[];
    let i = 0;
    for (const col of cols) {
      if (until(col, i)) return [col, i];
      i += col.colSpan;
    }
    return [cols.at(-1)!, i];
  };

  const getIndexOfColumn = (col: HTMLTableCellElement): number => {
    const row = col.parentElement;
    if (!(row instanceof HTMLTableRowElement)) return -1;
    return countColumns(row, (c) => col === c)[1];
  };

  const getColumnByIndex = (row: HTMLTableRowElement, i: number): HTMLTableCellElement => {
    return countColumns(row, ({ colSpan }, j) => j <= i && i < j + colSpan)[0];
  };

  const hoverColumn = (index: number | undefined, highlight: boolean) => {
    if (index === undefined) return;
    const rows = [...(wrapper?.querySelectorAll('tr') ?? [])] as HTMLTableRowElement[];
    for (const row of rows) {
      const col = getColumnByIndex(row, index);
      if (highlight) col?.classList.add('column-hover');
      else col?.classList.remove('column-hover');
    }
  };

  const parentMatching = (el: Element | null, query: string): Element | null => {
    while (el !== null && !el.matches(query)) el = el.parentElement;
    return el;
  };

  let hoveredColumn: number | undefined;
  const unhover = () => {
    hoverColumn(hoveredColumn, false);
    hoveredColumn = undefined;
  };

  const onMouseMove = (e: MouseEvent) => {
    unhover();
    const col = parentMatching(e.target as Element, 'th, td');
    if (!(col instanceof HTMLTableCellElement)) return;
    const newIndex = getIndexOfColumn(col);
    if (hoveredColumn === newIndex) return;
    hoveredColumn = newIndex;
    hoverColumn(hoveredColumn, true);
  };

  const tableClasses = `bg-base-100 table-fixed border-collapse border-spacing-0 w-fit text-sm`;
</script>

{#snippet colgroup()}
  <colgroup>
    {#each columnWidths as w, i (i)}
      <col style="width: {typeof w === 'number' ? `${w}px` : w};" />
    {/each}
  </colgroup>
{/snippet}

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  style="clip-path: inset(0 0 -100vh 0);{styles?.mainWrapper ?? ''}"
  class={[
    'wrapper relative min-h-max overflow-x-auto overflow-y-visible',
    showBottom ? 'mb-24' : 'mb-4',
    classes.mainWrapper,
    fakebarVisible && 'scrollbar-hidden',
  ]}
  onmousemove={onMouseMove}
  onmouseleave={unhover}
  onscroll={wrapperScroll}
  bind:this={wrapper}
>
  <!-- Sticky top header clone -->
  <div class={['top overflow-hidden', !showTop && 'hidden']} bind:this={top}>
    {#if header}
      <table class={[tableClasses, 'relative']} style="min-width: {totalWidth}px;">
        {@render colgroup()}
        <thead class="border-b-2 border-gray-200 dark:border-gray-700">
          {@render header()}
        </thead>
      </table>
    {/if}
  </div>

  <!-- Main table -->
  <table class={[tableClasses, 'overflow-visible']} style="min-width: {totalWidth}px;">
    {@render colgroup()}
    <thead class="border-b-2 border-gray-200 dark:border-gray-700">
      {@render header?.()}
    </thead>
    <tbody style="display: block; position: relative; height: {totalSize}px;">
      {@render children?.()}
    </tbody>
  </table>

  <!-- Fixed bottom bar (selected aggregate) -->
  <div
    style="bottom: {fakebarVisible ? 12 : 0}px;"
    class={['bottom overflow-hidden', classes.bottomWrapper, !showBottom && 'hidden']}
    bind:this={bottom}
  >
    {#if bottomHeader !== undefined || bottomBody !== undefined}
      <table class={[tableClasses, 'relative']} style="min-width: {totalWidth}px;">
        {@render colgroup()}
        <thead>
          {@render bottomHeader?.()}
        </thead>
        <tbody>
          {@render bottomBody?.()}
        </tbody>
      </table>
    {/if}
  </div>

  {#if !disableStickyScrollbar}
    <FakeScrollbar monitored={wrapper} visible={fakebarVisible} />
  {/if}
</div>

<style lang="postcss">
  @reference "tailwindcss";

  .top,
  .bottom {
    will-change: transform;
    position: fixed;
    z-index: 10;
  }

  .bottom {
    bottom: 8px;
    box-shadow:
      0px -5px 5.3px rgba(0, 0, 0, 0.069),
      0px -7px 17.9px rgba(0, 0, 0, 0.101),
      0px -10px 80px rgba(0, 0, 0, 0.17);
  }

  .top {
    top: calc(var(--header-height) + var(--header-offset, 0px) + 24px);
    box-shadow:
      0px 1.8px 5.3px rgba(0, 0, 0, 0.028),
      0px 6px 17.9px rgba(0, 0, 0, 0.042),
      0px 27px 80px rgba(0, 0, 0, 0.07);
  }
</style>
